import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { applyPageMeta } from '../lib/seo';
import { PAGE_META } from '../lib/routeMeta';
import { getEventState, getEventWeeks } from '../lib/api/event';
import type { EventWeek } from '../lib/api/event';
import { useAuth } from '../lib/auth/AuthContext';
import type { AuthUser } from '../lib/auth/types';
import { TERMS_VERSION } from '../lib/legalVersions';
import {
  bucketSeconds,
  elapsedSignupSeconds,
  endSignupClock,
  startSignupClock,
} from '../lib/signupTiming';
import { trackEvent } from '../lib/plausible';
import { LandingStep } from '../components/register/LandingStep';
import { EmailStep } from '../components/register/EmailStep';
import { CodeStep } from '../components/register/CodeStep';
import { ProfileStep } from '../components/register/ProfileStep';
import { ConsentStep } from '../components/register/ConsentStep';
import { FormError } from '../components/form/FormError';
import { RegisterLayout } from '../components/register/RegisterLayout';

type Step = 'landing' | 'email' | 'code' | 'profile' | 'consent';

/**
 * The /register flow. Single route, internal step state — back-button leaves
 * the flow entirely. Step state is local to this component (the URL never
 * changes); the parent /play route is the gate that decides who lands here.
 *
 * Plausible:
 *   - `Signup Step` fires on each successful advance with {step, durationBucket}
 *   - `Signup` fires on terminal completion with {durationBucket}
 *
 * The signup clock starts the first time we leave the landing step (the user
 * has actually engaged with the flow) and ends on terminal completion.
 */
export default function RegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const reduce = useReducedMotion();

  const [step, setStep] = useState<Step>(() => (auth.isAuthenticated ? 'profile' : 'landing'));
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [country, setCountry] = useState('');
  const [eventWeeks, setEventWeeks] = useState<EventWeek[] | null>(null);
  const [currentWeek, setCurrentWeek] = useState<number | null>(null);
  const [bootError, setBootError] = useState<string | null>(null);
  const resendCooldownUntilRef = useRef<number>(0);

  // Auth-state reactions: once the user authenticates we resync the step.
  // Only PENDING users have business on this page — anyone else (ACTIVE,
  // SUSPENDED, or a future status the FE doesn't know about) gets handed
  // off to /play, which has the canonical gates for those states.
  useEffect(() => {
    if (auth.status === 'loading') return;
    if (auth.isAuthenticated) {
      if (auth.user?.status !== 'PENDING') {
        endSignupClock();
        navigate('/play', { replace: true });
        return;
      }
      // PENDING — resume at the profile step. Pre-fill any names we already have.
      setStep((prev) => (prev === 'landing' || prev === 'email' || prev === 'code' ? 'profile' : prev));
      setFirstName((prev) => prev || auth.user?.firstName || '');
      setLastName((prev) => prev || auth.user?.lastName || '');
      setCountry((prev) => prev || auth.user?.country || '');
    }
  }, [auth.status, auth.isAuthenticated, auth.user, navigate]);

  // The AppBar "Log in" link points at /register with state.resetTo === 'landing'.
  // Since the step lives in internal state and the URL never changes, navigating
  // to /register while already mounted here doesn't remount or reset anything —
  // so we honor the flag explicitly and snap an anonymous user back to the
  // landing step (GHS5-57). Guarded on !isAuthenticated so it never fights the
  // auth-resync effect above (which moves PENDING users to the profile step);
  // the "Log in" link only renders for anonymous visitors anyway. We clear the
  // history state after consuming it so a refresh / back-forward doesn't re-fire
  // the reset. Keyed on location.key so it runs on every nav, including the
  // same-path click that React Router still mints a fresh key for.
  useEffect(() => {
    if (location.state?.resetTo === 'landing' && !auth.isAuthenticated) {
      setStep('landing');
      navigate('.', { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Load event week info up front so we know the current rulesVersion +
  // weekNumber by the time the user reaches the consent step. Cheap public
  // endpoints; no auth required.
  useEffect(() => {
    let cancelled = false;
    Promise.all([getEventState(), getEventWeeks()])
      .then(([state, weeks]) => {
        if (cancelled) return;
        setCurrentWeek(state.currentWeek);
        setEventWeeks(weeks.weeks);
      })
      .catch((err) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn('[GH] /register boot fetch failed', err);
        setBootError(
          "We couldn't load the current week's information. Please refresh and try again.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    applyPageMeta(PAGE_META.register);
  }, []);

  const currentRules = useMemo(() => {
    if (!eventWeeks || currentWeek === null) return null;
    return eventWeeks.find((w) => w.weekNumber === currentWeek)?.rules ?? null;
  }, [eventWeeks, currentWeek]);

  const recordStepAdvance = useCallback((from: Step) => {
    const seconds = elapsedSignupSeconds();
    trackEvent('Signup Step', {
      step: from,
      durationBucket: bucketSeconds(seconds),
    });
  }, []);

  const handleAdvanceEmail = useCallback(
    (acceptedEmail: string) => {
      // Reset the resend cooldown if the user is sending to a different
      // address than last time — the old cooldown was for the old email's
      // request, not this one.
      if (acceptedEmail !== email) resendCooldownUntilRef.current = 0;
      setEmail(acceptedEmail);
      recordStepAdvance('email');
      setStep('code');
    },
    [email, recordStepAdvance],
  );

  const handleAdvanceProfile = useCallback(
    (first: string, last: string, selectedCountry: string) => {
      setFirstName(first);
      setLastName(last);
      setCountry(selectedCountry);
      recordStepAdvance('profile');
      setStep('consent');
    },
    [recordStepAdvance],
  );

  const handleVerified = useCallback(
    (user: AuthUser) => {
      recordStepAdvance('code');
      // Hand the freshly-verified user to AuthContext synchronously — same
      // pattern as handleConsentComplete. Eliminates the /auth/refresh
      // round-trip and any race against the next mount.
      auth.setUser(user);
      if (user.status === 'PENDING') {
        // New user — pre-fill any name fields the API already has and move
        // on to the profile step.
        setFirstName((prev) => prev || user.firstName);
        setLastName((prev) => prev || user.lastName);
        setCountry((prev) => prev || user.country || '');
        setStep('profile');
        return;
      }
      // ACTIVE, SUSPENDED, or anything else — registration is done from
      // this flow's perspective. /play has the canonical gates for each
      // status (game / 'not eligible' / etc.).
      endSignupClock();
      navigate('/play', { replace: true });
    },
    [auth, navigate, recordStepAdvance],
  );

  const handleConsentComplete = useCallback(
    (user: AuthUser) => {
      const seconds = elapsedSignupSeconds();
      trackEvent('Signup Step', { step: 'consent', durationBucket: bucketSeconds(seconds) });
      trackEvent('Signup', { durationBucket: bucketSeconds(seconds) });
      endSignupClock();
      // CRITICAL: update AuthContext SYNCHRONOUSLY with the freshly-registered
      // user (now ACTIVE) before navigating. Earlier this called `void
      // auth.refresh()` which fires the round-trip but doesn't block — so
      // /play would mount while auth.user.status was still the stale
      // PENDING, PlayPage would bounce back to /register, RegisterPage would
      // briefly show ProfileStep, then the refresh would finally complete
      // and we'd hop forward again. Using setUser eliminates the round-trip
      // (the access token doesn't change — `status` isn't encoded in it) so
      // the next mount of /play sees ACTIVE on the first read.
      auth.setUser(user);
      navigate('/play', { replace: true });
    },
    [auth, navigate],
  );

  // Start the clock the first time the user actually engages with the form
  // (i.e. leaves the landing step). Idempotent.
  useEffect(() => {
    if (step !== 'landing') startSignupClock();
  }, [step]);

  // If the boot fetch failed we can't render any step safely — the consent
  // step needs `currentRules` and `currentWeek`, and downstream steps depend
  // on the user not getting halfway through before discovering the failure.
  // Block the whole form panel with the error message.
  if (bootError) {
    return (
      <RegisterLayout>
        <FormError>{bootError}</FormError>
      </RegisterLayout>
    );
  }

  // Crossfade between steps — short, no layout movement, opt-out under
  // prefers-reduced-motion.
  const slide = reduce
    ? ({ initial: false } as const)
    : ({
        initial: { opacity: 0, y: 8 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -8 },
        transition: { duration: 0.25, ease: 'easeOut' as const },
      } as const);

  return (
    <div className="w-full">
      <AnimatePresence mode="wait" initial={false}>
        {step === 'landing' && (
          <motion.div key="landing" {...slide}>
            <LandingStep
              onContinue={() => {
                // Track the landing→email transition so the funnel captures
                // anonymous-engagement drop-off. No timing bucket here
                // because the signup clock starts when we leave landing.
                trackEvent('Signup Step', { step: 'landing', durationBucket: '<10s' });
                setStep('email');
              }}
            />
          </motion.div>
        )}
        {step === 'email' && (
          <motion.div key="email" {...slide}>
            <EmailStep
              initialEmail={email}
              onBack={() => setStep('landing')}
              onAdvance={handleAdvanceEmail}
            />
          </motion.div>
        )}
        {step === 'code' && (
          <motion.div key="code" {...slide}>
            <CodeStep
              email={email}
              onBack={() => setStep('email')}
              onVerified={handleVerified}
              resendCooldownUntilRef={resendCooldownUntilRef}
            />
          </motion.div>
        )}
        {step === 'profile' && (
          <motion.div key="profile" {...slide}>
            <ProfileStep
              initialFirstName={firstName}
              initialLastName={lastName}
              initialCountry={country}
              onAdvance={handleAdvanceProfile}
            />
          </motion.div>
        )}
        {step === 'consent' && (
          <motion.div key="consent" {...slide}>
            {currentRules && currentWeek !== null ? (
              <ConsentStep
                weekNumber={currentWeek}
                rulesVersion={currentRules.version}
                termsVersion={TERMS_VERSION}
                firstName={firstName}
                lastName={lastName}
                country={country}
                onBack={() => setStep('profile')}
                onComplete={handleConsentComplete}
              />
            ) : eventWeeks !== null ? (
              // Boot fetch succeeded but the current week has no rules row —
              // misconfigured DB or a week before any rules were seeded.
              // Surface it as an error rather than spinning forever.
              <RegisterLayout>
                <FormError>
                  We couldn't find this week's rules. Please refresh and try again, or contact
                  support if this keeps happening.
                </FormError>
              </RegisterLayout>
            ) : (
              <RegisterLayout>
                <p className="font-['Arial',sans-serif] text-center text-white">
                  Loading the current week's rules…
                </p>
              </RegisterLayout>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
